import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) nav("/app/projects");
    else setErr(r.error);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAF9F4]">
      <div className="hidden lg:block relative bg-black">
        <img
          src="https://images.pexels.com/photos/14989321/pexels-photo-14989321.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Construction site"
          className="absolute inset-0 w-full h-full object-cover opacity-55"
        />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <Logo size={26} inverse />
          <div>
            <p className="label-eyebrow flex items-center text-[#D4B570]">
              <span className="gold-rule" />{t("tagline")}
            </p>
            <h1 className="mt-4 font-[Fraunces] text-5xl leading-[1.05] max-w-md text-white"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 60' }}>
              {t("hero_sub")}
            </h1>
          </div>
        </div>
      </div>

      <div className="flex flex-col p-6 lg:p-12">
        <div className="flex justify-end">
          <LanguageToggle />
        </div>
        <div className="flex-1 flex items-center">
          <form onSubmit={submit} className="w-full max-w-sm mx-auto space-y-6" data-testid="login-form">
            <div>
              <p className="label-eyebrow flex items-center"><span className="gold-rule" />{t("sign_in")}</p>
              <h2 className="mt-3 font-[Fraunces] font-medium text-4xl text-neutral-950">
                {t("login_title")}
              </h2>
              <p className="text-neutral-500 mt-3 text-sm">{t("login_sub")}</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="label-eyebrow">{t("email")}</Label>
              <Input id="email" name="obra-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="off" data-testid="login-email-input" className="h-11" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-eyebrow">{t("password")}</Label>
              <Input id="password" name="obra-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required autoComplete="new-password" data-testid="login-password-input" className="h-11" />
            </div>
            {err && <p className="text-sm text-red-600" data-testid="login-error">{err}</p>}
            <Button type="submit" disabled={loading}
              className="w-full bg-black hover:bg-neutral-800 rounded-full h-11 text-sm"
              data-testid="login-submit-button">
              {loading ? "…" : t("sign_in")}
            </Button>
            <p className="text-sm text-neutral-500">
              {t("no_account")}{" "}
              <Link to="/register" className="text-[#B8933A] font-medium hover:text-[#8C6D22]" data-testid="link-to-register">
                {t("sign_up")}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
